<template>
  <div class="content getting-started-wrapper max-w-4xl mx-auto p-6 flex">
    <!-- Sidebar menu -->
    <nav class="sidebar mr-8 sticky top-6 self-start">
      <ul>
        <li><a href="#create-account">1. Create an account</a></li>
        <li><a href="#connect-github">2. Connect Github</a></li>
        <li><a href="#deploy-app">3. Deploy your app</a></li>
        <li><a href="#enter-payment-info">4. Enter payment information</a></li>
        <li><a href="#build-status">5. Build status</a></li>
        <li><a href="#dns-setup">6. DNS setup</a></li>
        <li><a href="#scaling-resizing">7. Scaling & Resizing</a></li>
        <li><a href="#update-delete-app">8. Update/Delete your app</a></li>
        <li><a href="#monitoring-logs">9. Monitoring & Logs</a></li>
      </ul>
    </nav>

    <!-- Main content -->
    <div class="getting-started-container flex-1">
      <h1 class="text-3xl font-bold mb-6">Getting Started</h1>

      <h5>In this tutorial, you will learn how to deploy an app using our platform. We will deploy a demo Node.js application.</h5>
      <p>Before starting, please fork this <a href="https://github.com/johnpapa/node-hello" target="_blank">repository</a> to your Github account. We will use it as our demo app in this tutorial.</p>

      <section id="create-account" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">1. Create an account</h2>
        <p class="mb-4">To deploy an app, you first need to create an account.</p>
        <ul class="list-disc pl-6">
          <li>Click on the <strong>Sign Up</strong> button.</li>
          <li>Fill in your details and create a new account.</li>
          <img src="../../public/getting-started/create-account.png" alt="Sign Up" class="mb-4">
          <li>Once your account is created, you can deploy your app.</li>
        </ul>
      </section>

      <section id="connect-github" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">2. Connect Github</h2>
        <p class="mb-4">After creating an account, you are redirected to the Deployments page.</p>
        <ul class="list-disc pl-6">
          <li>Click on the <strong>Deploy new app</strong> button.</li>
          <img src="../../public/getting-started/deploy-new-app.png" alt="Deploy new app" class="mb-4">
          <li>You have to connect your <strong>Github</strong> account.</li>
          <img src="../../public/getting-started/sign-in-with-github.png" alt="Connect Github" class="mb-4">
          <li>You’ll be redirected to Github to authorize access.</li>
          <li>Once authorized, your repositories will be available for deployment.</li>
        </ul>
      </section>

      <section id="deploy-app" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">3. Deploy your app</h2>
        <p class="mb-4">You are automatically redirected to the Deploy page. You have to enter some information:</p>
        <ul class="list-disc pl-6">
          <li><strong>App name:</strong> Give your app a unique name</li>
          <li><strong>Region:</strong> Choose one or multiple regions to deploy your app to. For this tutorial, select one region of your choice</li>
          <li><strong>Size:</strong> Pick the instance size that matches your need. For this tutorial, the <code>XS</code> instance is sufficient</li>
          <li><strong>Repo:</strong> Since you connected your Github account, your repos will be listed here. For this tutorial, select the <code>node-hello</code> repo you forked</li>
          <li><strong>Branch:</strong> Enter the branch to deploy. For this tutorial, enter <code>master</code></li>
          <li><strong>Language:</strong> Select your runtime language. For this tutorial, select <code>Node.js</code></li>
          <li><strong>Version:</strong> Select your runtime version. For this tutorial, enter <code>22.5.1</code></li>
        <img src="../../public/getting-started/deploy-app-1.png" alt="Deploy app 1" class="mb-4">
          <li><strong>Folder:</strong> If you use a mono-repo, specify the folder containing your app (e.g., <code>front</code> or <code>back</code>). For this tutorial, leave it to <code>root</code></li>
          <li><strong>Install command:</strong> Choose the command to install your app's dependencies. For this tutorial, enter <code>npm install</code></li>
          <li><strong>Start command:</strong> Choose the command to launch your app. For this tutorial, enter <code>npm run start</code></li>
          <li><strong>Port:</strong> Specify the port your app will run on. For this tutorial, enter <code>3000</code></li>
          <li><strong>Environment variables:</strong> Enter the environment variables your app needs to run. For this tutorial, enter <code>NODE_ENV=production</code></li>
          <li><strong>Domain name:</strong> Enter your custom domain name. For this tutorial, enter a subdomain of a domain you own (e.g. <code>tutorial.your-domain.com</code>)</li>
        <img src="../../public/getting-started/deploy-app-2.png" alt="Deploy app 2" class="mb-4">
        <li>Click <strong>Deploy</strong> to launch your app 🚀</li>
        </ul>
      </section>

      <section id="enter-payment-info" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">4. Enter payment information</h2>
        <p class="mb-4">If it's your first deployment, you will have to enter your payment information.</p>
        <ul class="list-disc pl-6">
          <li>Every new account gets a 7 days free trial. You will only be charged after the free trial period is over. You can always cancel your subscription before the trial ends.</li>
          <li>Choose your preferred payment method and enter payment information. We use <strong>Stripe</strong> as our payment provider.</li>
          <img src="../../public/getting-started/enter-payment-details.png" alt="Enter payment details" class="mb-4">
          <li>Click <strong>Submit</strong>. You may have to verify your payment method (e.g. 3D Secure).</li>
        </ul>
      </section>

      <section id="build-status" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">5. Build status</h2>
        <p class="mb-4">You are automatically redirected to the deployment page where you see the status of your build.</p>
        <ul class="list-disc pl-6">
          <li>You can see all your builds on the right side.</li>
          <img src="../../public/getting-started/deployment-attempts.png" alt="Deployment attempts" class="mb-4">
          <li>Click on <strong>View Logs</strong> to see the build logs.</li>
          <img src="../../public/getting-started/build-logs.png" alt="Build logs" class="mb-4">
          <li>Once the build is over, you can see the status of your deployment.</li>
          <img src="../../public/getting-started/deployment-status.png" alt="Deployment status" class="mb-4">
        </ul>
      </section>

      <section id="dns-setup" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">6. DNS setup</h2>
        <p class="mb-4">You need to enter the right DNS records to point to your new deployment.</p>
        <ul class="list-disc pl-6">
          <li>Update the DNS records at your domain provider to point to your new deployment. Wait some minutes for the changes to propagate, then click <strong>Update DNS</strong>.</li>
          <img src="../../public/getting-started/update-dns.png" alt="Update DNS" class="mb-4">
          <li>Your DNS status becomes <strong>Active</strong>. An SSL certificate is automatically generated and managed. Your app is now accessible online on the provided domain.</li>
          <img src="../../public/getting-started/dns-active.png" alt="DNS Active" class="mb-4">
        </ul>
      </section>

      <section id="scaling-resizing" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">7. Scaling & Resizing</h2>
        <p class="mb-4">You can scale and resize your deployment at any time. These are managed at a region level.</p>
        <ul class="list-disc pl-6">
          <li>Select the region of your choice in the top bar.</li>
          <img src="../../public/getting-started/select-region.png" alt="Select Region" class="mb-4">
          <li>To scale this specific region, adjust the number of containers in the <strong>Scale</strong> section.</li>
          <img src="../../public/getting-started/scale-region.png" alt="Scale Region" class="mb-4">
          <li>To resize the container size in this specific region, adjust the <strong>Size</strong> option.</li>
          <img src="../../public/getting-started/resize-region.png" alt="Resize Region" class="mb-4">
          <li>If your container shutdowns unexpectedly, you can see the reasons on the right side. If the container ran out of memory, increase the <strong>Size</strong> option.</li>
          <img src="../../public/getting-started/container-shutdown.png" alt="Container Shutdown" class="mb-4">
        </ul>
      </section>

      <section id="update-delete-app" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">8. Update/Delete your app</h2>
        <p class="mb-4">You can update/delete your app at any time.</p>
        <ul class="list-disc pl-6">
          <li>Modify any information you wish, and click on <strong>Update and deploy</strong>.</li>
          <img src="../../public/getting-started/update-app-1.png" alt="Update App 1">
          <img src="../../public/getting-started/update-app-2.png" alt="Update App 2" class="mb-4">
          <li>To delete your app, click on <strong>Delete app</strong>. Warning: this action is irreversible.</li>
          <img src="../../public/getting-started/delete-app.png" alt="Delete App" class="mb-4">
        </ul>
      </section>

      <section id="monitoring-logs" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">9. Monitoring & Logs</h2>
        <p class="mb-4">You can monitor your app's performance and health at any time.</p>
        <ul class="list-disc pl-6">
          <li>Click on <strong>View app logs</strong> to view real-time logs from your app</li>
          <img src="../../public/getting-started/view-app-logs.png" alt="View App Logs" class="mb-4">
          <img src="../../public/getting-started/app-logs.png" alt="App Logs" class="mb-4">
          <li>Click on <strong>View metrics</strong> to see real-time CPU and memory usage.</li>
          <img src="../../public/getting-started/view-metrics.png" alt="View Metrics" class="mb-4">
          <img src="../../public/getting-started/metrics.png" alt="Metrics" class="mb-4">
        </ul>
      </section>

      <h5>Your demo Node.js app is now live. You can now deploy any app of yours. Have fun!</h5>

      <router-link :to="{ name: user.email ? 'Deployments' : 'SignUp' }">
        <button class="cta">Get started now</button>
      </router-link>
    </div>
  </div>
</template>

<script>

  export default {
    name: 'GettingStarted',
    components: {  },
    props: {

    },
    data() {
      return {

      }
    },
    created() {

    },
    computed: {
      user() {
        return this.$store.state.loggedUser;
      },
    },
    methods: {

    }
  }
</script>

<style scoped>
.getting-started-wrapper {
  display: flex;
  gap: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  color: #222;
}

.sidebar {
  position: sticky;
  top: 1rem; /* or whatever offset you want */
  align-self: flex-start;
  background: #f9f9f9;
  padding: 1rem;
  border-radius: 6px;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.05);
}

.sidebar ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.sidebar ul li {
  margin-bottom: 1rem;
}

.sidebar ul li a {
  color: #3498db;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
}

.sidebar ul li a:hover {
  text-decoration: underline;
}

.getting-started-container {
  flex: 1 1 auto;
}

@media (max-width: 768px) {
  .getting-started-wrapper {
    flex-direction: column;
  }

  .sidebar {
    position: relative; /* disable sticky on mobile */
    top: auto;
    width: 100%;
    margin-bottom: 1rem;
  }
}
</style>